import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Input, InputNumber, Modal, Select, Space, Switch, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ObjectUtils from "../../../../../../util/ObjectUtils";
import StringUtils from "../../../../../../util/StringUtils";
import ProductApi from "../../../../../../api/ProductApi";
import GatewayApi from "../../../../../../api/GatewayApi";
import PermissionWrapper from "../../../../../../components/permission/PermissionWrapper";
import { GatewaySettingsWrapper, GroupContent, GroupTitle, SettingDescription, SettingItem, SettingTitle, SettingValue, Settinglabel, SettingsGroup } from "./GatewaySettings.styled";


interface GatewaySettingsProps {
    gatewayView: any
}

const GatewaySettings: React.FC<GatewaySettingsProps> = (props)=> {
    let { gatewayView } = props;
    let settingsValue = gatewayView.gateway.settings;
    let [ searchParams ] = useSearchParams();
    let advanced = searchParams.get('advanced');

    // api
    let productApi = new ProductApi();
    let gatewayApi = new GatewayApi();
    const [messageApi, contextHolder] = message.useMessage();

    // state
    let [gatewaySettings, setGatewaySettings] = useState(StringUtils.isEmpty(settingsValue) ? {} as any: JSON.parse(settingsValue));
    let [product, setProduct] = useState(null);
    let [settingsGroups, setSettingsGroups] = useState([]);

    let [openSystemSettingsModal, setOpenSystemSettingsModal] = useState(false)
    let [editingSystemSettings, setEditingSystemSettings] = useState(null);

    let [openHeartBeatSettingsModal, setOpenHeartBeatSettingsModal] = useState(false);
    let [editingHeartBeatSettings, setEditingHeartBeatSettings] = useState(null);

    let [openAdcSettingsModal, setOpenAdcSettingsModal] = useState(false);
    let [editingAdcSettings, setEditingAdcSettings] = useState(null);

    let [openUartSettingsModal, setOpenUartSettingsModal] = useState(false);
    let [editingUartInterface, setEditingUartInterface] = useState(null);
    let [editingUartSettings, setEditingUartSettings] = useState(null);

    let [openCanSettingsModal, setOpenCanSettingsModal] = useState(false);
    let [editingCanSettings, setEditingCanSettings] = useState(null);

    let [openCustomizedSettingsModal, setOpenCustomizedSettingsModal] = useState(false);
    let [editingCustomizedSettings, setEditingCustomizedSettings] = useState(null);
    
    // load product specifications
    let loadProductModel = async ()=> {
        // get product
        let product = (await productApi.getProductByModel(gatewayView.node.model)).data.data;
        setProduct(product);

        // parse settings
        let settings = JSON.parse(product.settings);
        let settingsGroups = settings.settingsGroups;
        setSettingsGroups(settingsGroups);
    };

    // effect
    useEffect(()=> {
        loadProductModel();
    }, []);

    // render
    let timeUnitOptions = [
        { value: 'ms', label: '毫秒' }, 
        { value: 's', label: '秒' }, 
        { value: 'min', label: '分' }, 
        { value: 'hour', label: '小时' },
    ];

    let getIntervalUnitText = (unit: string)=> {
        if (unit == null) {
            unit = '';
        }
        unit = unit.toLowerCase();
        switch(unit) {
            case 'ms': 
                return 'ms';
            case 's':
                return '秒';
            case 'min':
                return '分';
            case 'hour':
                return '小时';
            case 'day':
                return '天';
        }
        return '';
    };

    let getSettingsGroupContent = (settingsGroup: any)=> {
        switch(settingsGroup.type.toLowerCase()) {
            case 'standard_system':
                return getSettingsGroupContentSystem(settingsGroup);
            case 'standard_heart_beat':
                return getSettingsGroupContentHeartBeat(settingsGroup);
            case 'standard_adc':
                return getSettingsGroupContentAdc(settingsGroup);
            case 'standard_uart':
                return getSettingsGroupContentUart(settingsGroup);
            case 'customized': 
                return getSettingsGroupContentCustomized(settingsGroup);
        }
        return <div></div>;
    };

    let getSettingsGroupContentSystem = (settingsGroup)=> {
        let saveSystemSettings = async ()=> {
            gatewaySettings.system = editingSystemSettings;
            await gatewayApi.saveSettings(gatewayView.device.id, settingsGroup.type, gatewaySettings.system);
            messageApi.open({
                type: 'success',
                content: '保存系统配置成功',
            });
            setOpenSystemSettingsModal(false);
        };

        let onOpenSystemSettingsModal = ()=> {
            let systemSettings = gatewaySettings.system;
            if (systemSettings == null) {
                systemSettings = {};
            }
            setEditingSystemSettings({...systemSettings});
            setOpenSystemSettingsModal(true);
        };

        let onHttpServerChange = (e)=> {
            editingSystemSettings.httpServer = e.target.value;
        };

        let onMqttServerChange = (e)=> {
            editingSystemSettings.mqttServer = e.target.value;
        };

        return (
            <SettingsGroup key={settingsGroup.key}>
                <GroupTitle>
                    {settingsGroup.name}
                    <PermissionWrapper permissionCodePath="device-management.device.update-device-settings">
                        <Button type='link' onClick={onOpenSystemSettingsModal} disabled={gatewayView.node.status == 'OFFLINE'}><EditOutlined />编辑</Button>
                    </PermissionWrapper>
                </GroupTitle>
                <GroupContent>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>HTTP服务器地址</SettingTitle>
                            <SettingDescription>用于设备注册，固件下载等操作，修改重启生生效</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{gatewaySettings.system?.httpServer}</SettingValue>
                    </SettingItem>

                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>MQTT服务器地址</SettingTitle>
                            <SettingDescription>用于采集数据传输，参数下载等操作，修改重启后生效</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{gatewaySettings.system?.mqttServer}</SettingValue>
                    </SettingItem>
                </GroupContent>
                <Modal open={openSystemSettingsModal} 
                    onClose={()=> setOpenSystemSettingsModal(false)} 
                    closable={false}
                    width={800}
                    height={600}
                    title="系统设置"
                    footer={
                        <Space size="middle">
                            <Button type="primary" onClick={()=> saveSystemSettings()}>保存</Button>
                            <Button type="primary" danger onClick={()=> setOpenSystemSettingsModal(false)}>取消</Button>
                        </Space>
                    }
                >
                    <GroupContent>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>HTTP服务器地址</SettingTitle>
                                <SettingDescription>用于设备注册，固件下载等操作，修改重启生生效</SettingDescription>
                            </Settinglabel>
                            <Input defaultValue={editingSystemSettings?.system?.httpServer} 
                                onChange={onHttpServerChange} 
                                style={{
                                    flex: '1 1 auto',
                                    margin: '0px 10px'
                                }}
                            />
                        </SettingItem>

                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>MQTT服务器地址</SettingTitle>
                                <SettingDescription>用于采集数据传输，参数下发等操作，修改重启后生效</SettingDescription>
                            </Settinglabel>
                            <Input defaultValue={editingSystemSettings?.system?.mqttServer} 
                                onChange={onMqttServerChange} 
                                style={{
                                    flex: '1 1 auto',
                                    margin: '0px 10px'
                                }}
                            />
                        </SettingItem>
                    </GroupContent>                    
                </Modal>
            </SettingsGroup>
        )
    };

    let getSettingsGroupContentHeartBeat = (settingsGroup)=> {
        let saveHeartBeatSettings = async ()=> {
            gatewaySettings.heartBeat = editingHeartBeatSettings;
            await gatewayApi.saveSettings(gatewayView.device.id, settingsGroup.type, gatewaySettings.heartBeat);
            messageApi.open({
                type: 'success',
                content: '保存GNSS/心跳配置成功',
            });
            setOpenHeartBeatSettingsModal(false);
        };

        let updateHeartBeatIntervalMs = ()=> {
            let interval = editingHeartBeatSettings.interval;
            let intervalUnit = editingHeartBeatSettings.intervalUnit;
            if (intervalUnit == null) {
                intervalUnit = 'ms';
            }
            let intervalMs = interval;
            switch(intervalUnit.toLowerCase()) {
                case 'ms':
                    intervalMs = interval;
                    break;
                case 's':
                    intervalMs = interval * 1000;
                    break;
                case 'min':
                    intervalMs = interval * 1000 * 60;
                    break;
                case 'hour':
                    intervalMs = interval * 1000 * 60 * 60;
                    break;
            }
            editingHeartBeatSettings.intervalMs = intervalMs;
            setEditingHeartBeatSettings({...editingHeartBeatSettings});
        };

        let onOpenHeartBeatSettingsModal = ()=> {
            let heartBeatSettings = gatewaySettings.heartBeat;
            if (heartBeatSettings == null) {
                heartBeatSettings = {};
            }
            setEditingHeartBeatSettings({...heartBeatSettings});
            setOpenHeartBeatSettingsModal(true);
        };

        let onHeartBeatIntervalChange = (value)=> {
            editingHeartBeatSettings.interval = parseInt(value);
            updateHeartBeatIntervalMs();
        };

        let onHeartBeatIntervalUnitChange = (value)=> {
            editingHeartBeatSettings.intervalUnit = value;
            updateHeartBeatIntervalMs();
        };

        let saveEnabled = editingHeartBeatSettings?.interval != null && !StringUtils.isEmpty(editingHeartBeatSettings?.intervalUnit);

        return (
            <SettingsGroup key={settingsGroup.key}>
                <GroupTitle>
                    {settingsGroup.name} 
                    <PermissionWrapper permissionCodePath="device-management.device.update-device-settings">
                        <Button type='link' onClick={onOpenHeartBeatSettingsModal} disabled={gatewayView.node.status == 'OFFLINE'}><EditOutlined />编辑</Button>
                    </PermissionWrapper>
                </GroupTitle>
                <GroupContent>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>GNSS/心跳上报时间间隔</SettingTitle>
                            <SettingDescription>设置GNSS 心跳数据包上报时间间隔，如需录制轨迹，请设置短一些的间隔以获取准确轨迹</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{gatewaySettings.heartBeat?.interval} {getIntervalUnitText(gatewaySettings.heartBeat?.intervalUnit)}</SettingValue>
                    </SettingItem>
                </GroupContent>

                <Modal open={openHeartBeatSettingsModal} 
                    onClose={()=> setOpenHeartBeatSettingsModal(false)} 
                    closable={false}
                    width={800}
                    height={600}
                    title="GNSS/心跳设置"
                    footer={
                        <Space size="middle">
                            <Button type="primary" disabled={!saveEnabled} onClick={()=> saveHeartBeatSettings()}>保存</Button>
                            <Button type="primary" danger onClick={()=> setOpenHeartBeatSettingsModal(false)}>取消</Button>
                        </Space>
                    }
                >
                    <GroupContent>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>GNSS/心跳上报时间间隔</SettingTitle>
                                <SettingDescription>设置GNSS 心跳数据包上报时间间隔，如需录制轨迹，请设置短一些的间隔以获取准确轨迹</SettingDescription>
                            </Settinglabel>
                            <InputNumber 
                                min={0}
                                max={999999}
                                defaultValue={editingHeartBeatSettings?.interval}
                                onChange={onHeartBeatIntervalChange}
                                addonAfter={
                                    <Select 
                                        style={{width: '70px'}}
                                        options={timeUnitOptions}
                                        defaultValue={editingHeartBeatSettings?.intervalUnit?.toLowerCase()} 
                                        onChange={onHeartBeatIntervalUnitChange} >
                                    </Select>
                                }
                                style={{
                                    flex: '1 1 auto',
                                    margin: '0px 10px'
                                }}
                            />
                        </SettingItem>
                    </GroupContent>
                </Modal>
            </SettingsGroup>
        )
    };

    let getSettingsGroupContentAdc = (settingsGroup)=> {
        let saveAdcSettings = async ()=> {
            gatewaySettings.adc = editingAdcSettings;
            await gatewayApi.saveSettings(gatewayView.device.id, settingsGroup.type, gatewaySettings.adc);
            messageApi.open({
                type: 'success',
                content: '保存ADC参数配置成功',
            });
            setOpenAdcSettingsModal(false);
        };

        let getChannelSettings = (channel: number, editing=false)=> {
            let adcSettings = editing ? editingAdcSettings : gatewaySettings.adc;
            if (adcSettings==null) {
                return null;
            }
            let channelSettings = adcSettings[`ch${channel}`];
            if (channelSettings==null) {
                return null;
            }
            return channelSettings;
        }

        let getChannelEnabled = (channel: number, editing = false)=> {
            let channelSettings = getChannelSettings(channel, editing);
            if (channelSettings ==  null) {
                return true; // default to true
            }
            return channelSettings.enabled;
        };

        let getChannelInterval = (channel: number, editing = false)=> {
            let channelSettings = getChannelSettings(channel, editing);
            if (channelSettings ==  null) {
                return '';
            }
            return channelSettings.publishInterval;
        }

        let getChannelIntervalUnit = (channel: number, editing = false)=> {
            let channelSettings = getChannelSettings(channel, editing);
            if (channelSettings ==  null) {
                return '';
            }
            return channelSettings.publishIntervalUnit?.toLowerCase();
        }

        let getChannelPublishIntervalText = (channel: number)=> {
            let adcSettings = gatewaySettings.adc;
            if (adcSettings==null) {
                return '/';
            }
            let channelSettings = adcSettings[`ch${channel}`];
            if (channelSettings==null) {
                return '/';
            }
            return `${StringUtils.nonull(channelSettings.publishInterval)} ${getIntervalUnitText(channelSettings.publishIntervalUnit)}`;
        };

        let getChannelSettingsCreate = (channel: number)=> {
            let adcSettings = editingAdcSettings;
            let channelSettings = adcSettings[`ch${channel}`];
            if (channelSettings==null) {
                channelSettings = {enabled: true};
                adcSettings[`ch${channel}`] = channelSettings;
            }
            return channelSettings;
        };

        let onOpenAdcSettingsModal = ()=> {
            let editingAdcSettings = gatewaySettings.adc;
            if (editingAdcSettings == null) {
                editingAdcSettings = {};
            }
            setEditingAdcSettings(ObjectUtils.copy(editingAdcSettings));
            setOpenAdcSettingsModal(true)
        };

        let onChannelEnableChange = (checked, channel)=> {
            let channelSettings = getChannelSettingsCreate(channel);
            channelSettings.enabled = checked;
            setEditingAdcSettings({...editingAdcSettings});
        };

        let updateChannelIntervalMs = (channel)=> {
            let channelSettings = getChannelSettingsCreate(channel)
            let interval = channelSettings.publishInterval;
            if (interval == null) {
                interval = 0;
            }

            let intervalUnit = channelSettings.publishIntervalUnit;
            if (intervalUnit == null) {
                intervalUnit = 'ms';
            }
            let intervalMs = interval;
            switch(intervalUnit.toLowerCase()) {
                case 'ms':
                    intervalMs = interval;
                    break;
                case 's':
                    intervalMs = interval * 1000;
                    break;
                case 'min':
                    intervalMs = interval * 1000 * 60;
                    break;
                case 'hour':
                    intervalMs = interval * 1000 * 60 * 60;
                    break;
            }

            channelSettings.publishIntervalMs = intervalMs;
            setEditingAdcSettings({...editingAdcSettings});
        };

        let checkMinimumPublishInterval = (channelSettings: any)=> {
            if (channelSettings.publishIntervalMs < 100) {
                messageApi.open({
                    type: 'error',
                    content: `上报时间间隔不能小于100ms`,
                });
            }
        };

        let onChannelIntervalChange = (value, channel)=> {
            let channelSettings = getChannelSettingsCreate(channel);
            channelSettings.publishInterval = parseInt(value);
            updateChannelIntervalMs(channel);
            checkMinimumPublishInterval(channelSettings);

            if (!advanced) {
                for(let i=1; i<=settingsGroup.channels; i++) {
                    let channelSettings = getChannelSettingsCreate(i);
                    channelSettings.publishInterval = parseInt(value);
                    updateChannelIntervalMs(i);
                }
            }
        };

        let onChannelIntervalUnitChange = (value, channel)=> {
            let channelSettings = getChannelSettingsCreate(channel);
            channelSettings.publishIntervalUnit = value;
            updateChannelIntervalMs(channel);
            checkMinimumPublishInterval(channelSettings);

            if (!advanced) {
                for(let i=1; i<=settingsGroup.channels; i++) {
                    let channelSettings = getChannelSettingsCreate(i);
                    channelSettings.publishIntervalUnit = value;
                    updateChannelIntervalMs(i);
                }
            }
        };

        let channels: number[] = [];
        for(let i=1; i<=settingsGroup.channels; i++) {
            channels.push(i);
        }

        let saveEnabled = true;
        if (editingAdcSettings) {
            for(let i=1; i<=settingsGroup.channels; i++) {
                let channelSettings = getChannelSettings(i, true);
                if (channelSettings == null) {
                    saveEnabled = false;
                    break;
                }
                if (channelSettings.enabled == false) {
                    // nop
                } else {
                    if (channelSettings.publishInterval == null || isNaN(channelSettings.publishInterval) || StringUtils.isEmpty(channelSettings.publishIntervalUnit)) {
                        saveEnabled = false;
                        break;
                    }
                    if (channelSettings.publishIntervalMs < 100) {
                        saveEnabled = false;
                        break;
                    }
                }
            }
        }

        return <SettingsGroup key={settingsGroup.key}>
            <GroupTitle>
                {settingsGroup.name} 
                <PermissionWrapper permissionCodePath="device-management.device.update-device-settings">
                    <Button type='link' onClick={onOpenAdcSettingsModal} disabled={gatewayView.node.status == 'OFFLINE'}><EditOutlined />编辑</Button>
                </PermissionWrapper>
            </GroupTitle>
            <GroupContent>
                {
                    channels.map((channel)=> {
                        return <SettingItem key={channel}>
                            <Settinglabel>
                                <SettingTitle>通道{channel}参数</SettingTitle>
                                <SettingDescription>设置启用/禁用，上报时间间隔</SettingDescription>
                            </Settinglabel>
                            <SettingValue>
                                <Switch disabled={true} checked={getChannelEnabled(channel)} style={{marginRight: '20px'}} /> 
                                上报时间间隔：{getChannelPublishIntervalText(channel)}
                            </SettingValue>
                        </SettingItem>
                    })
                }
            </GroupContent>
            
            <Modal open={openAdcSettingsModal} 
                onClose={()=> setOpenAdcSettingsModal(false)} 
                closable={false}
                width={1000}
                height={600}
                title="ADC参数设置"
                footer={
                    <Space size="middle">
                        <Button type="primary" disabled={ !saveEnabled } onClick={()=> saveAdcSettings()}>保存</Button>
                        <Button type="primary" danger onClick={()=> setOpenAdcSettingsModal(false)}>取消</Button>
                    </Space>
                }
            >
                <GroupContent>
                    {
                        channels.map((channel)=> {
                            return <SettingItem key={channel}>
                                <Settinglabel>
                                    <SettingTitle>通道{channel}参数</SettingTitle>
                                    <SettingDescription>设置启用/禁用，上报时间间隔</SettingDescription>
                                </Settinglabel>
                                <SettingValue>
                                    <Switch checked={getChannelEnabled(channel, true)} 
                                    style={{marginRight: '20px'}}
                                    onChange={(checked)=> onChannelEnableChange(checked, channel)}
                                /> 
                                    <div style={{flex: '0 0 auto'}}>上报时间间隔：</div>
                                    <InputNumber 
                                        min={0}
                                        max={999999}
                                        changeOnBlur={true}
                                        onChange={(e)=> onChannelIntervalChange(e, channel)}
                                        defaultValue={getChannelInterval(channel, true)}
                                        value={getChannelInterval(channel, true)}
                                        
                                        addonAfter={
                                            <Select 
                                                options={timeUnitOptions}
                                                defaultValue={getChannelIntervalUnit(channel, true)} 
                                                value={getChannelIntervalUnit(channel, true)}
                                                style={{width: '70px'}}
                                                onChange={(e)=> {
                                                    onChannelIntervalUnitChange(e, channel);
                                                }}
                                            >
                                            </Select>
                                        }
                                        style={{
                                            flex: '1 1 auto',
                                            margin: '0px 10px'
                                        }}
                                    />
                                </SettingValue>
                            </SettingItem>
                        })
                    }
                </GroupContent>
            </Modal>
        </SettingsGroup>
    };

    let getSettingsGroupContentUart = (settingsGroup)=> {
        let saveUartSettings = async ()=> {
            let uart = {};
            uart[`${editingUartInterface}`] = editingUartSettings;
            gatewaySettings.uart = uart;
            await gatewayApi.saveSettings(gatewayView.gateway.id, settingsGroup.type, gatewaySettings.uart);
            messageApi.open({
                type: 'success',
                content: '保存串口配置成功',
            });
            setOpenUartSettingsModal(false);
            setEditingUartInterface(null);
            setEditingUartSettings(null);
        };

        let onOpenUartSettingsModal = (inf: number)=> {
            let uartSettings = gatewaySettings.uart;
            if (uartSettings == null) {
                uartSettings = {};
            }
            let editingUartSettings = uartSettings[`${inf}`];
            if (editingUartSettings == null) {
                editingUartSettings = {};
            }

            setEditingUartInterface(`${inf}`);
            setEditingUartSettings({...editingUartSettings});
            setOpenUartSettingsModal(true);
        };

        let onUartBaudrateChange = (value)=> {
            editingUartSettings.baudrate = value;
            setEditingUartSettings({...editingUartSettings});
        };

        let onUartDataBitsChange = (value)=> {
            editingUartSettings.dataBits = value;
            setEditingUartSettings({...editingUartSettings});
        };

        let onUartStopBitsChange = (value)=> {
            editingUartSettings.stopBits = value;
            setEditingUartSettings({...editingUartSettings});
        };

        let onUartParityChange = (value)=> {
            editingUartSettings.parity = value;
            setEditingUartSettings({...editingUartSettings});
        };

        let onUartBufferSizeChange = (value)=> {
            editingUartSettings.bufferSize = value;
            setEditingUartSettings({...editingUartSettings});
        };

        let onUartBufferTimeChange = (value)=> {
            editingUartSettings.bufferTime = value;
            setEditingUartSettings({...editingUartSettings});
        }

        let baudrateOptions = [
            {label: '1200', value: 1200},
            {label: '2400', value: 2400},
            {label: '4800', value: 4800},
            {label: '9600', value: 9600},
            {label: '19200', value: 19200},
            {label: '38400', value: 38400},
            {label: '57600', value: 57600},
            {label: '115200', value: 115200},
        ];

        let dataBitOptions = [
            {label: '5', value: 5},
            {label: '6', value: 6},
            {label: '7', value: 7},
            {label: '8', value: 8}
        ];

        let stopBitOptions = [
            {label: '1', value: 1},
            {label: '2', value: 2}
        ];

        let parityOptions = [
            {label: '无校验位', value: 'none'},
            {label: '奇校验', value: 'odd'},
        ];

        let saveEnabled = editingUartSettings.baudrate != null 
            && editingUartSettings.dataBits != null
            && editingUartSettings.stopBits != null
            && editingUartSettings.parity != null
            && editingUartSettings.bufferSize != null
            && editingUartSettings.bufferTime != null;
        
        let uarts = [];
        for (let i=0; i<settingsGroup.interfaces; i++) {
            uarts.push(i);
        }

        return uarts.map(e=> {
            let uartInterfaecSettings = gatewaySettings.uart[`${e}`];
            return <SettingsGroup key={settingsGroup.key + '-' + e}>
                <GroupTitle>
                    {settingsGroup.name + ' ' + (e+1)} 
                    <PermissionWrapper permissionCodePath="gateway-management.gateway.update-gateway-settings">
                        <Button type='link' onClick={()=> onOpenUartSettingsModal(e)} disabled={gatewayView.node.status == 'OFFLINE'}><EditOutlined />编辑</Button>
                    </PermissionWrapper>
                </GroupTitle>
                <GroupContent>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>波特率</SettingTitle>
                            <SettingDescription>串口通信的传输速率单位，异步串行通信场景下，通信双方需预先约定一致的波特率参数以保障正常通信</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.baudrate}</SettingValue>
                    </SettingItem>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>数据位</SettingTitle>
                            <SettingDescription>单帧报文承载的有效数据长度，异步串行通信场景下，通信双方需预先约定一致的数据位参数以保障正常通信</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.dataBits}</SettingValue>
                    </SettingItem>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>停止位</SettingTitle>
                            <SettingDescription>用于标识单帧报文结束的信号位长度，异步串行通信场景下，通信双方需预先约定一致的停止位参数以保障正常通信</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.stopBits}</SettingValue>
                    </SettingItem>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>奇偶校验位</SettingTitle>
                            <SettingDescription>用于数据完整性校验的附加位，异步串行通信场景下，通信双方需预先约定一致的奇偶校验位参数以保障正常通信</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.parity}</SettingValue>
                    </SettingItem>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>数据缓冲区大小（字节）</SettingTitle>
                            <SettingDescription>缓存待发送数据的最大容量阈值，当缓冲区存储数据达到设定值时，将自动触发数据发送操作</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.bufferSize}</SettingValue>
                    </SettingItem>
                    <SettingItem>
                        <Settinglabel>
                            <SettingTitle>数据缓冲时间阈值（毫秒）</SettingTitle>
                            <SettingDescription>数据缓存的最长等待时长，当缓存时间达到设定阈值时，无论缓冲区是否存满，均触发数据发送操作</SettingDescription>
                        </Settinglabel>
                        <SettingValue>{uartInterfaecSettings.bufferTime}</SettingValue>
                    </SettingItem>
                </GroupContent>

                <Modal open={openHeartBeatSettingsModal} 
                    onClose={()=> setOpenUartSettingsModal(false)} 
                    closable={false}
                    width={800}
                    height={600}
                    title="UART设置"
                    footer={
                        <Space size="middle">
                            <Button type="primary" disabled={!saveEnabled} onClick={()=> saveUartSettings()}>保存</Button>
                            <Button type="primary" danger onClick={()=> setOpenUartSettingsModal(false)}>取消</Button>
                        </Space>
                    }
                >
                    <GroupContent>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>波特率</SettingTitle>
                                <SettingDescription>串口通信的传输速率单位，异步串行通信场景下，通信双方需预先约定一致的波特率参数以保障正常通信</SettingDescription>
                            </Settinglabel>
                            <Select 
                                style={{flex: '1 1 auto'}}
                                options={baudrateOptions} >
                                defaultValue={editingUartSettings?.baudrate} 
                                onChange={onUartBaudrateChange}
                            </Select>
                        </SettingItem>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>数据位</SettingTitle>
                                <SettingDescription>单帧报文承载的有效数据长度，异步串行通信场景下，通信双方需预先约定一致的数据位参数以保障正常通信</SettingDescription>
                            </Settinglabel>
                            <Select 
                                style={{flex: '1 1 auto'}}
                                options={dataBitOptions}
                                defaultValue={editingUartSettings?.dataBits} 
                                onChange={onUartDataBitsChange}>
                            </Select>
                        </SettingItem>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>停止位</SettingTitle>
                                <SettingDescription>用于标识单帧报文结束的信号位长度，异步串行通信场景下，通信双方需预先约定一致的停止位参数以保障正常通信</SettingDescription>
                            </Settinglabel>
                            <Select 
                                style={{flex: '1 1 auto'}}
                                options={stopBitOptions}
                                defaultValue={editingUartSettings?.stopBits} 
                                onChange={onUartStopBitsChange}>
                            </Select>
                        </SettingItem>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>奇偶校验位</SettingTitle>
                                <SettingDescription>用于数据完整性校验的附加位，异步串行通信场景下，通信双方需预先约定一致的奇偶校验位参数以保障正常通信</SettingDescription>
                            </Settinglabel>
                            <Select 
                                style={{flex: '1 1 auto'}}
                                options={parityOptions}
                                defaultValue={editingUartSettings?.parity} 
                                onChange={onUartParityChange}>
                            </Select>
                        </SettingItem>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>数据缓冲区大小（字节）</SettingTitle>
                                <SettingDescription>缓存待发送数据的最大容量阈值，当缓冲区存储数据达到设定值时，将自动触发数据发送操作</SettingDescription>
                            </Settinglabel>
                            <InputNumber 
                                min={1}
                                max={1024}
                                precision={0}
                                stringMode={true}
                                step={1}
                                defaultValue={editingUartSettings.bufferSize} 
                                onChange={onUartBufferSizeChange}
                                style={{
                                    flex: '1 1 auto',
                                    margin: '0px 10px'
                                }}
                            />
                        </SettingItem>
                        <SettingItem>
                            <Settinglabel>
                                <SettingTitle>数据缓冲时间阈值（毫秒）</SettingTitle>
                                <SettingDescription>缓存待发送数据的最大容量阈值，当缓冲区存储数据达到设定值时，将自动触发数据发送操作</SettingDescription>
                            </Settinglabel>
                            <InputNumber 
                                min={0}
                                max={1000}
                                precision={0}
                                stringMode={true}
                                step={1}
                                defaultValue={editingUartSettings.bufferTime} 
                                onChange={onUartBufferTimeChange}
                                style={{
                                    flex: '1 1 auto',
                                    margin: '0px 10px'
                                }}
                            />
                        </SettingItem>
                    </GroupContent>
                </Modal>
            </SettingsGroup>
        });
    };

    let getSettingsGroupContentCustomized = (settingsGroup)=> {
        let saveCustomizedSettings = async ()=> {
            gatewaySettings.customized = editingCustomizedSettings;
            await gatewayApi.saveSettings(gatewayView.device.id, settingsGroup.type, gatewaySettings.customized);
            messageApi.open({
                type: 'success',
                content: `保存${settingsGroup.name}配置成功`,
            });
            setOpenCustomizedSettingsModal(false);
        };

        let onOpenCustomizedSettingsModal = ()=> {
            let customizedSettings = gatewaySettings.customized;
            if (customizedSettings == null) {
                customizedSettings = {};
            }
            setEditingCustomizedSettings({...customizedSettings});
            setOpenCustomizedSettingsModal(true);
        };

        let onCustomizedSettingItemValueChange = (settingItem, value)=> {
            editingCustomizedSettings[settingItem.key] = value;
            setEditingCustomizedSettings({...editingCustomizedSettings});
        };

        let getCustomizedSettingItemRenderValue = (customizedSettings: any, settingItem: any)=> {
            if (customizedSettings==null) {
                return '';
            }
            if (settingItem.type=='BOOL') {
                return <Switch checked={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem.key)} 
                    style={{marginRight: '20px'}}
                    disabled={true}
                />
            }
            return customizedSettings[settingItem.key];
        };

        let getCustomizedSettingItemValue = (customizedSettings: any, settingItem: any)=> {
            if (customizedSettings==null) {
                return '';
            }
            return customizedSettings[settingItem.key];
        };

        
        let saveEnabled = true;
        if (editingCustomizedSettings) {
            for(let i=0; i<settingsGroup.settingItems.length; i++) {
                let settingItem = settingsGroup.settingItems[i];
                let settingItemValue = getCustomizedSettingItemValue(editingCustomizedSettings, settingItem);
                if (StringUtils.isEmpty(settingItemValue)) {
                    if (settingItem.nullable !=null && settingItem.nullable) {
                        // nop
                    } else {
                        saveEnabled = false;
                        break;
                    }
                }
            }
        }

    return (
            <SettingsGroup key={settingsGroup.key}>
                <GroupTitle>
                    {settingsGroup.name} 
                    <PermissionWrapper permissionCodePath="device-management.device.update-device-settings">
                        <Button type='link' onClick={onOpenCustomizedSettingsModal} disabled={gatewayView.node.status == 'OFFLINE'}><EditOutlined />编辑</Button>
                    </PermissionWrapper>
                </GroupTitle>
                <GroupContent>
                    {
                        settingsGroup.settingItems.map((settingItem)=> {
                            return <SettingItem key={settingItem.key}>
                                <Settinglabel>
                                    <SettingTitle>{settingItem.name}</SettingTitle>
                                    <SettingDescription>{settingItem.description}</SettingDescription>
                                </Settinglabel>
                                <SettingValue>{getCustomizedSettingItemRenderValue(gatewaySettings.customized, settingItem)}</SettingValue>
                            </SettingItem>
                        })
                    }
                </GroupContent>

                <Modal open={openCustomizedSettingsModal} 
                    onClose={()=> setOpenCustomizedSettingsModal(false)} 
                    closable={false}
                    width={800}
                    height={600}
                    title={settingsGroup.name}
                    footer={
                        <Space size="middle">
                            <Button type="primary" disabled={!saveEnabled} onClick={()=> saveCustomizedSettings()}>保存</Button>
                            <Button type="primary" danger onClick={()=> setOpenCustomizedSettingsModal(false)}>取消</Button>
                        </Space>
                    }
                >
                    <GroupContent>
                        { settingsGroup.settingItems.map((settingItem)=> {
                            return <SettingItem>
                                <Settinglabel>
                                    <SettingTitle>{settingItem.name}</SettingTitle>
                                    <SettingDescription>{settingItem.description}</SettingDescription>
                                </Settinglabel>
                                {
                                    (()=> {
                                        if (settingItem.type=='INT') {
                                            return <InputNumber 
                                                min={settingItem.min}
                                                max={settingItem.max}
                                                precision={0}
                                                stringMode={true}
                                                step={1}
                                                defaultValue={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem)}
                                                onChange={(value)=> onCustomizedSettingItemValueChange(settingItem, value)}
                                                style={{
                                                    flex: '1 1 auto',
                                                    margin: '0px 10px'
                                                }}
                                            />
                                        } else if (settingItem.type=='FLOAT') {
                                            return <InputNumber 
                                                min={settingItem.min}
                                                max={settingItem.max}
                                                stringMode={true}
                                                defaultValue={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem)}
                                                onChange={(value)=> onCustomizedSettingItemValueChange(settingItem, value)}
                                                style={{
                                                    flex: '1 1 auto',
                                                    margin: '0px 10px'
                                                }}
                                            />
                                        } else if (settingItem.type=='STRING') {
                                            return <Input 
                                                defaultValue={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem)}
                                                onChange={(e)=> onCustomizedSettingItemValueChange(settingItem, e.target.value)}
                                                style={{
                                                    flex: '1 1 auto',
                                                    margin: '0px 10px'
                                                }}
                                            />
                                        } else if (settingItem.type=='BOOL') {
                                            return <Switch checked={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem.key)} 
                                                style={{marginRight: '20px'}}
                                                onChange={(checked)=> onCustomizedSettingItemValueChange(settingItem, checked)}
                                            />
                                        } else {
                                            return <Input 
                                                defaultValue={getCustomizedSettingItemValue(editingCustomizedSettings, settingItem)}
                                                onChange={(e)=> onCustomizedSettingItemValueChange(settingItem, e.target.value)}
                                                style={{
                                                    flex: '1 1 auto',
                                                    margin: '0px 10px'
                                                }}
                                            />
                                        }
                                    })()
                                }
                            </SettingItem>
                        }) }
                    </GroupContent>
                </Modal>
            </SettingsGroup>
        )
    };

    return (
        <GatewaySettingsWrapper>
            {
                settingsGroups.map((settingsGroup)=> {
                    return <div key={settingsGroup.key}>
                        { getSettingsGroupContent(settingsGroup) }
                    </div>
                })
            }
            {contextHolder}
        </GatewaySettingsWrapper>
    )
}

export default GatewaySettings;
